import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const Auterive = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });
  const [contentRef, contentInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.1 });
  const [guaranteeRef, guaranteeInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation Fenêtres Auterive 31190 | Vitrier Expert Haute-Garonne | Intervention Rapide';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Réparation fenêtres à Auterive (31190). Expert vitrier pour fenêtres PVC, bois, alu. Intervention rapide sous 2h. Devis gratuit. ☎ 05 54 54 25 45');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'réparation fenêtre Auterive, vitrier Auterive 31190, dépannage fenêtre Auterive, double vitrage Auterive, fenêtre PVC Auterive, fenêtre bois Auterive, volets roulants Auterive');
    }

    // Schema.org LocalBusiness
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Réparation Fenêtres Auterive - Vitrerie 31",
      "image": "https://reparation-fenetres-toulouse.fr/auterive.jpg",
      "telephone": "+33554542545",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Auterive",
        "postalCode": "31190",
        "addressRegion": "Haute-Garonne",
        "addressCountry": "FR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 43.3506,
        "longitude": 1.4778
      },
      "areaServed": {
        "@type": "City",
        "name": "Auterive"
      },
      "priceRange": "€€",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "43"
      }
    });
    document.head.appendChild(script);

    // Schema.org FAQPage
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Quel est le délai d'intervention à Auterive ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nous intervenons sous 2h à Auterive pour toute urgence de réparation de fenêtres. Notre équipe locale est disponible 24h/24 et 7j/7."
          }
        },
        {
          "@type": "Question",
          "name": "Quels types de fenêtres réparez-vous à Auterive ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nous réparons tous types de fenêtres : PVC, bois, aluminium, double vitrage, fenêtres anciennes, volets roulants et baies vitrées à Auterive."
          }
        },
        {
          "@type": "Question",
          "name": "Proposez-vous un devis gratuit à Auterive ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, nous proposons un devis gratuit et sans engagement pour toute réparation de fenêtres à Auterive. Contactez-nous au 05 54 54 25 45."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    // Schema.org BreadcrumbList
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": "https://reparation-fenetres-toulouse.fr/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Auterive",
          "item": "https://reparation-fenetres-toulouse.fr/auterive"
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
      document.head.removeChild(breadcrumbScript);
    };
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Marie Dupont',
      location: 'Centre-ville Auterive',
      text: 'Réparation rapide de mes fenêtres anciennes. Travail soigné et respect du patrimoine. Très satisfaite !',
      rating: 5
    },
    {
      name: 'Pierre Garonne',
      location: 'Quartier Gare',
      text: 'Problème d\'humidité résolu grâce au remplacement du double vitrage. Équipe professionnelle et efficace.',
      rating: 5
    },
    {
      name: 'Sophie Martin',
      location: 'Quartier Église',
      text: 'Intervention en urgence un dimanche. Service impeccable et prix honnête. Je recommande vivement !',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-water-flash-line', title: 'Humidité Garonne', desc: 'Condensation et moisissures' },
    { icon: 'ri-ancient-gate-line', title: 'Fenêtres anciennes', desc: 'Patrimoine à préserver' },
    { icon: 'ri-wind-line', title: 'Infiltrations d\'air', desc: 'Joints usés par le temps' },
    { icon: 'ri-contrast-drop-line', title: 'Vitrage endommagé', desc: 'Bris de glace et fissures' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Auterive - Haute-Garonne (31190)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">Auterive</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Expert en <strong>rénovation de fenêtres anciennes</strong> et traitement anti-humidité
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 2h à Auterive</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">À 30 km de Toulouse</h3>
                <p className="text-gray-600 text-sm">Tous quartiers desservis</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">Auterive</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experts en réparation de fenêtres pour tous les quartiers d'Auterive
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes fréquents à <strong className="text-teal-600">Auterive</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Solutions adaptées aux spécificités de votre ville
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Pourquoi ces problèmes à Auterive ?</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>30 km au sud de Toulouse</strong> sur les bords de l'Ariège, Auterive possède un riche <strong>patrimoine architectural ancien</strong> nécessitant des interventions spécialisées. La proximité de la <strong>Garonne et de l'Ariège</strong> crée une humidité importante affectant les fenêtres. Nos équipes interviennent dans tous les quartiers : <strong>Centre-ville, Gare, Église, Moulin</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">Auterive</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAU BLOC 1 - Rénovation Fenêtres Anciennes */}
      <section ref={contentRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={contentInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/3fa8eff519b05632a55adbd7777b51d2.jpg"
                  alt="Rénovation fenêtres anciennes Auterive patrimoine"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-ancient-gate-line text-teal-600 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">Patrimoine</p>
                      <p className="text-sm text-gray-600">Expert restauration</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={contentInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                <strong className="text-teal-600">Rénovation de Fenêtres Anciennes</strong> à Auterive
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Auterive possède un <strong>patrimoine architectural remarquable</strong> avec de nombreuses maisons anciennes et bâtiments historiques. Notre expertise en <strong>rénovation de fenêtres anciennes à Auterive</strong> nous permet de préserver l'authenticité de votre patrimoine tout en améliorant les performances thermiques et acoustiques de vos menuiseries.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Nous réalisons la <strong>restauration de fenêtres en bois anciennes</strong>, le <strong>remplacement de vitrage simple par double vitrage sur mesure</strong>, la <strong>réparation de ferrures d'époque</strong> et la <strong>rénovation de volets battants traditionnels</strong>. Nos artisans respectent les techniques traditionnelles et les contraintes du patrimoine historique d'Auterive.
              </p>

              <div className="bg-gradient-to-r from-amber-50 to-teal-50 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-building-line text-amber-600 mr-2 text-xl"></i>
                  Respect du Patrimoine Auterivain
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center text-gray-700">
                    <i className="ri-check-line text-amber-600 mr-3"></i>
                    <span className="text-sm">Conformité aux règles d'urbanisme et ABF</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <i className="ri-check-line text-amber-600 mr-3"></i>
                    <span className="text-sm">Préservation des caractéristiques d'origine</span>
                  </div>
                  <div className="flex items-center text-gray-700">
                    <i className="ri-check-line text-amber-600 mr-3"></i>
                    <span className="text-sm">Techniques traditionnelles de menuiserie</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-hammer-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Restauration Bois Massif</h4>
                    <p className="text-gray-600 text-sm">Traitement, ponçage, réparation et finition des fenêtres en bois ancien</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-window-2-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Double Vitrage sur Mesure</h4>
                    <p className="text-gray-600 text-sm">Adaptation de double vitrage fin pour conserver l'esthétique d'origine</p>
                  </div>
                </div>
              </div>

              <a
                href="#contact"
                className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Devis Rénovation Patrimoine
              </a>
            </motion.div>
          </div>

          {/* NOUVEAU BLOC 2 - Traitement Anti-Humidité */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={contentInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="order-2 lg:order-1"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                <strong className="text-orange-600">Traitement Anti-Humidité</strong> des Fenêtres à Auterive
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                La proximité de l'<strong>Ariège et de la Garonne</strong> crée un environnement humide à Auterive, particulièrement dans les quartiers proches des cours d'eau. Cette humidité provoque <strong>condensation, moisissures et dégradation des fenêtres</strong>. Notre expertise en <strong>traitement anti-humidité fenêtres à Auterive</strong> vous garantit des solutions durables et efficaces.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Nous proposons l'<strong>installation de double vitrage anti-condensation</strong>, le <strong>remplacement de joints d'étanchéité</strong>, la <strong>pose de grilles d'aération</strong> et le <strong>traitement des menuiseries contre l'humidité</strong>. Nos solutions permettent d'éliminer la condensation, prévenir les moisissures et améliorer la qualité de l'air intérieur à Auterive.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-water-flash-line text-blue-600 mr-2 text-xl"></i>
                  Diagnostic Humidité Gratuit
                </h4>
                <p className="text-gray-700 text-sm mb-4">
                  Nous analysons l'origine de l'humidité et vous proposons les solutions adaptées à votre situation à Auterive.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600 mb-1">-70%</p>
                    <p className="text-xs text-gray-600">Condensation réduite</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-teal-600 mb-1">100%</p>
                    <p className="text-xs text-gray-600">Moisissures éliminées</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-drop-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Vitrage Anti-Condensation</h4>
                    <p className="text-gray-600 text-sm">Double vitrage à isolation renforcée avec gaz argon pour éliminer la condensation</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-wind-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Ventilation Intégrée</h4>
                    <p className="text-gray-600 text-sm">Grilles d'aération discrètes pour renouveler l'air et évacuer l'humidité</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Traitement Préventif</h4>
                    <p className="text-gray-600 text-sm">Protection des menuiseries bois contre l'humidité et les champignons</p>
                  </div>
                </div>
              </div>

              <a
                href="#contact"
                className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Diagnostic Gratuit
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={contentInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative order-1 lg:order-2"
            >
              <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=window%20moisture%20condensation%20treatment%20showing%20before%20and%20after%20comparison%20anti%20humidity%20solution%20with%20water%20droplets%20being%20eliminated%20modern%20dehumidification%20technology%20for%20windows%20in%20auterive%20near%20river%20area%20simple%20clean%20background&width=800&height=600&seq=auterive-humidity-treatment-v1&orientation=landscape"
                  alt="Traitement anti-humidité fenêtres Auterive Garonne"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-6 right-6 bg-white rounded-xl shadow-xl p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-water-flash-line text-blue-600 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">Anti-Humidité</p>
                      <p className="text-sm text-gray-600">Solution durable</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* NOUVEAU BLOC 3 - Notre Processus d'Intervention */}
          <div ref={processRef} className="mb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={processInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Notre <strong className="text-teal-600">Processus d'Intervention</strong> à Auterive
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Une méthode éprouvée pour des réparations de qualité
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  step: '1',
                  icon: 'ri-phone-line',
                  title: 'Appel & Diagnostic',
                  desc: 'Vous nous contactez, nous analysons votre besoin et planifions l\'intervention sous 2h à Auterive'
                },
                {
                  step: '2',
                  icon: 'ri-car-line',
                  title: 'Déplacement Rapide',
                  desc: 'Notre équipe se déplace avec tout le matériel nécessaire pour intervenir efficacement'
                },
                {
                  step: '3',
                  icon: 'ri-tools-line',
                  title: 'Réparation Expert',
                  desc: 'Intervention professionnelle avec des matériaux de qualité et techniques éprouvées'
                },
                {
                  step: '4',
                  icon: 'ri-checkbox-circle-line',
                  title: 'Contrôle Qualité',
                  desc: 'Vérification complète, nettoyage du chantier et garantie de nos travaux'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={processInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-teal-500 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {item.step}
                  </div>
                  <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <i className={`${item.icon} text-3xl text-teal-600`}></i>
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2 text-center">{item.title}</h4>
                  <p className="text-gray-600 text-sm text-center">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-teal-50 to-orange-50 rounded-xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-teal-600 mb-2">&lt; 2h</div>
                  <p className="text-gray-700 font-semibold">Délai d'intervention</p>
                  <p className="text-gray-600 text-sm">À Auterive et environs</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-orange-600 mb-2">15+</div>
                  <p className="text-gray-700 font-semibold">Années d'expérience</p>
                  <p className="text-gray-600 text-sm">Expertise reconnue</p>
                </div>
                <div>
                  <div className="text-4xl font-bold text-teal-600 mb-2">100%</div>
                  <p className="text-gray-700 font-semibold">Clients satisfaits</p>
                  <p className="text-gray-600 text-sm">Note moyenne 4.9/5</p>
                </div>
              </div>
            </div>
          </div>

          {/* NOUVEAU BLOC 4 - Nos Garanties */}
          <div ref={guaranteeRef} className="mb-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={guaranteeInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nos <strong className="text-orange-600">Garanties</strong> à Auterive
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Votre tranquillité d'esprit est notre priorité
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={guaranteeInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 shadow-lg"
              >
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-4xl text-teal-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Garantie Décennale</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Tous nos travaux de <strong>réparation et installation de fenêtres à Auterive</strong> sont couverts par notre <strong>assurance décennale</strong>. Cette garantie vous protège pendant 10 ans contre tout vice ou malfaçon affectant la solidité de l'ouvrage.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span className="text-sm">Protection juridique complète</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span className="text-sm">Réparation gratuite en cas de défaut</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                    <span className="text-sm">Attestation fournie après travaux</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={guaranteeInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-8 shadow-lg"
              >
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-medal-line text-4xl text-orange-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Garantie Satisfaction</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Nous nous engageons à votre <strong>satisfaction totale</strong>. Si vous n'êtes pas pleinement satisfait de notre intervention à Auterive, nous revenons gratuitement pour corriger le problème jusqu'à votre entière satisfaction.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span className="text-sm">Retour gratuit sous 30 jours</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span className="text-sm">Service après-vente réactif</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-orange-600 mr-2 mt-1"></i>
                    <span className="text-sm">Écoute et conseil personnalisé</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={guaranteeInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 shadow-lg"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-price-tag-3-line text-4xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Prix Transparents</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  <strong>Devis gratuit et détaillé</strong> avant toute intervention à Auterive. Nos tarifs sont clairs, sans surprise ni frais cachés. Vous savez exactement ce que vous payez avant le début des travaux.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                    <span className="text-sm">Devis détaillé gratuit</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                    <span className="text-sm">Aucun frais caché</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                    <span className="text-sm">Paiement après satisfaction</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={guaranteeInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-gradient-to-br from-green-50 to-white rounded-xl p-8 shadow-lg"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-time-line text-4xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Disponibilité 24h/24</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Notre service d'<strong>urgence fenêtre à Auterive</strong> est disponible 24h/24, 7j/7, même les week-ends et jours fériés. Une vitre cassée ? Un volet bloqué ? Nous intervenons rapidement.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                    <span className="text-sm">Intervention sous 2h</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                    <span className="text-sm">Équipe d'astreinte permanente</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <i className="ri-check-line text-green-600 mr-2 mt-1"></i>
                    <span className="text-sm">Sécurisation immédiate</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>

          {/* NOUVEAU BLOC 5 - Comparatif Matériaux */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Quel <strong className="text-teal-600">Matériau</strong> Choisir à Auterive ?
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Comparatif des différents types de fenêtres pour votre projet
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-teal-500 transition-all">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-tree-line text-3xl text-amber-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Fenêtres Bois</h3>
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Esthétique</span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-500 text-sm"></i>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Isolation</span>
                    <div className="flex gap-1">
                      {[...Array(4)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-500 text-sm"></i>
                      ))}
                      <i className="ri-star-line text-gray-300 text-sm"></i>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Entretien</span>
                    <div className="flex gap-1">
                      {[...Array(2)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-500 text-sm"></i>
                      ))}
                      {[...Array(3)].map((_, i) => (
                        <i key={i} className="ri-star-line text-gray-300 text-sm"></i>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Durabilité</span>
                    <div className="flex gap-1">
                      {[...Array(4)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-500 text-sm"></i>
                      ))}
                      <i className="ri-star-line text-gray-300 text-sm"></i>
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 mb-2"><strong>Idéal pour :</strong></p>
                  <p className="text-sm text-gray-600">Maisons anciennes, patrimoine, charme authentique</p>
                </div>
                <p className="text-center text-2xl font-bold text-amber-600 mb-2">À partir de 450€</p>
                <p className="text-center text-sm text-gray-600">Par fenêtre installée</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-teal-500 hover:border-teal-600 transition-all relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Le plus populaire
                </div>
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-window-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Fenêtres PVC</h3>
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Esthétique</span>
                    <div className="flex gap-1">
                      {[...Array(4)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-teal-500 text-sm"></i>
                      ))}
                      <i className="ri-star-line text-gray-300 text-sm"></i>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Isolation</span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-teal-500 text-sm"></i>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Entretien</span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-teal-500 text-sm"></i>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Durabilité</span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-teal-500 text-sm"></i>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="bg-teal-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 mb-2"><strong>Idéal pour :</strong></p>
                  <p className="text-sm text-gray-600">Meilleur rapport qualité-prix, entretien facile</p>
                </div>
                <p className="text-center text-2xl font-bold text-teal-600 mb-2">À partir de 350€</p>
                <p className="text-center text-sm text-gray-600">Par fenêtre installée</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-orange-500 transition-all">
                <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <i className="ri-building-line text-3xl text-gray-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Fenêtres Aluminium</h3>
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Esthétique</span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-gray-600 text-sm"></i>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Isolation</span>
                    <div className="flex gap-1">
                      {[...Array(4)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-gray-600 text-sm"></i>
                      ))}
                      <i className="ri-star-line text-gray-300 text-sm"></i>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Entretien</span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-gray-600 text-sm"></i>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Durabilité</span>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-gray-600 text-sm"></i>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 mb-2"><strong>Idéal pour :</strong></p>
                  <p className="text-sm text-gray-600">Design moderne, grandes baies vitrées</p>
                </div>
                <p className="text-center text-2xl font-bold text-gray-600 mb-2">À partir de 550€</p>
                <p className="text-center text-sm text-gray-600">Par fenêtre installée</p>
              </div>
            </div>

            <div className="mt-8 bg-gradient-to-r from-teal-500 to-orange-500 rounded-xl p-6 text-white text-center">
              <p className="text-lg font-semibold mb-2">Besoin de conseils pour choisir ?</p>
              <p className="mb-4 opacity-90">Nos experts vous guident gratuitement dans votre choix à Auterive</p>
              <a
                href="tel:0554542545"
                className="inline-block bg-white text-teal-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Conseil Gratuit : 05 54 54 25 45
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers d'<strong className="text-teal-600">Auterive</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre-ville', 'Quartier Gare', 'Quartier Église', 'Moulin', 'Ariège', 'Garonne', 'Plaine', 'Coteaux'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à Auterive ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 2h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/colomiers" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Colomiers</a>
            <span className="text-gray-400">•</span>
            <a href="/blagnac" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Blagnac</a>
            <span className="text-gray-400">•</span>
            <a href="/tournefeuille" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Tournefeuille</a>
            <span className="text-gray-400">•</span>
            <a href="/muret" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Muret</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
            <span className="text-gray-400">•</span>
            <a href="/cugnaux" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Cugnaux</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default Auterive;
